<template>
  <section
    ref="topicTabsRef"
    class="topic-tabs"
    :class="{ 'topic-tabs-sticky': isSticky }"
  >
    <div class="topic-tabs-inner">
      <Swiper
        space-between="10"
        slides-per-view="auto"
        :free-mode="true"
        :modules="swiperModules"
        center-insufficient-slides
      >
        <SwiperSlide
          v-for="(channel, index) in topicChannelsWithSymbol"
          :key="channel.symbolKey"
        >
          <div
            class="topic-channel-box"
            :class="{
              'topic-channel-active-box': index === currentChannelIndex,
            }"
            @click="toChangeSlide(index)"
          >
            <div class="topic-channel-text">{{ channel.name }}</div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/free-mode';
import { FreeMode } from 'swiper/modules';
import { browerVersion, uuid } from '../utils';

const currentChannelIndex = inject<Ref<number>>('currentChannelIndex');
defineOptions({
  name: 'TopicTabs',
});
const swiperModules = ref([FreeMode]);
const topicChannels =
  inject<Ref<{ id: string; name: string; topicId: string }[]>>('topicChannels');
const topicChannelsWithSymbol = computed(() => {
  if (!topicChannels || !topicChannels.value) {
    return [];
  }
  return topicChannels.value.map((item) => ({
    symbolKey: uuid(),
    ...item,
  }));
});

const topicTabsRef = ref<HTMLElement>();
const emits = defineEmits<{ changeSlide: [number] }>();
const toChangeSlide = (index: number) => {
  emits('changeSlide', index);
};

const isSticky = inject<Ref<boolean>>('isSticky');
const { top } = useElementBounding(topicTabsRef);
watch(
  () => top.value,
  (newVal, oldVal) => {
    if (oldVal > newVal && newVal < 1 && isSticky) {
      isSticky.value = true;
    }
  },
);
const swiperPadding = ref('0 20px 5px');
onMounted(() => {
  if (browerVersion().mobile) {
    swiperPadding.value = '0 15px 5px';
  }
});
</script>

<style lang="scss" scoped>
.topic-tabs {
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  height: 66px;
  margin-top: -33px;

  .topic-tabs-inner {
    width: 100%;
    height: 100%;
    padding: v-bind('swiperPadding');
    background: var(--tabs-background);
    background-size: 100% 100%;
  }

  :deep(.swiper) {
    height: 100%;

    .swiper-slide {
      width: auto;

      .topic-channel-box {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        height: 100%;
        cursor: pointer;

        .topic-channel-text {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 30px;
          padding: 0 15px;
          font-size: 0.875rem;
          color: var(--tab-color);
          border: 1px solid var(--tab-border-color);
          border-radius: 15px;
        }
      }

      .topic-channel-active-box {
        .topic-channel-text {
          color: var(--tab-active-color);
          background: var(--tab-active-backgound);
          background-size: 100% 100%;
          border: 1px none transparent;
        }
      }
    }
  }
}

.topic-tabs-sticky {
  background: var(--tabs-sticky-background);
}
</style>
